import React, { Fragment, useEffect, useState } from "react";
import styles from "../Components/Contactus/ContactInquiries.module.css";

import { ContactService } from "../../Logic/ContactService";
import type { IInquiryProps } from "@model/contactus.model";

import { useContactInquiries } from "../../Logic/Functions/Hooks/useContactInquiries";
import Chevron from "Utils/Assets/SVGs/Chevron";
import SearchBar from "../Components/UILibrary/SearchBar";
import ContactInquiryCard from "../Components/Contactus/ContactInquiryCard";
import ReplyToInquiryModal from "../Components/Modals/ReplyToInquiryModal";
import { UserAuthenticationStoreImplementation } from "Data/UserAuthentication/UserAuthenticationStoreImplementation";
import { useForceLogoutAndRedirect } from "../../Logic/Functions/Hooks/useForceLogoutAndRedirect";
import { UnauthorizedPopupStoreImplementation } from "Data/UnauthorizedPopup/UnauthorizedPopupStoreImplementation";
import { useNavigate } from "react-router-dom";

type Filter = "all" | "resolved" | "unresolved";
const toStatus = (f: Filter): "all" | "true" | "false" =>
  f === "resolved" ? "true" : f === "unresolved" ? "false" : "all";

const ContactInquiries: React.FC = () => {
  const contactService = new ContactService();
  const forceLogoutAndRedirect = useForceLogoutAndRedirect();

  const {
    filteredInquiries,
    searchQuery,
    setSearchQuery,
    page,
    setPage,
    totalPages,
    perPage,
    perPageOptions,
    perPageDropdownOpen,
    perPageDropdownActiveIndex,
    perPageDropdownWrapRef,
    perPageDropdownBtnRef,
    perPageDropdownListRef,
    togglePerPageDropdown,
    onPerPageDropdownButtonKeyDown,
    onPerPageDropdownListKeyDown,
    selectPerPageAt,
    requestParams,
    applyServerResponse,
    filter,
    filterOpen,
    setFilterOpen,
    filterRef,
    chooseFilter,
    expanded,
    toggleExpand,
    allCount,
    resCount,
    unresCount,
    formatWhen,
  } = useContactInquiries({ initialPerPage: 10 });

  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [replyOpen, setReplyOpen] = useState(false);
  const [activeInquiry, setActiveInquiry] = useState<IInquiryProps | null>(null);
  const { showPopup } = UnauthorizedPopupStoreImplementation();
  const navigate = useNavigate();
  const authStore = UserAuthenticationStoreImplementation();
  const token = authStore.authenticationState?.token ?? "";

  const handleOpenReply = (inq: IInquiryProps) => {
    setActiveInquiry(inq);
    setReplyOpen(true);
  };

  const handleCloseReply = () => {
    setReplyOpen(false);
    setActiveInquiry(null);
  };

  const handleResolve = (inq: IInquiryProps) => {
    contactService
      .handleResolveInquiry({
        id: inq.id,
        token,
        setApiError,
        onAuthError: forceLogoutAndRedirect,
        onForbidden: (msg) => {
          showPopup(msg);
          navigate("/", { replace: true });
        },
        onSuccess: () => {
          const updated = filteredInquiries.map((item) =>
            item.id === inq.id ? { ...item, resolved: true } : item
          );


          applyServerResponse({
            items: updated,
            meta: {
              resolvedTotal: resCount + 1,
              unresolvedTotal: Math.max(0, unresCount - 1),
            },
          });
        },
      })
      .catch(() => {
      });
  };

  const handleReplySuccess = (inquiryId: string) => {
    const updated = filteredInquiries.map((item) =>
      item.id === inquiryId ? { ...item, resolved: true } : item
    );

    applyServerResponse({
      items: updated,
      meta: {
        resolvedTotal: resCount + 1,
        unresolvedTotal: Math.max(0, unresCount - 1),
      },
    });
  };

  useEffect(() => {
    const status = toStatus(filter);

    contactService
      .handleGetContactMessages({
        token,
        onAuthError: forceLogoutAndRedirect,
        onForbidden: (msg) => {
          showPopup(msg);
          navigate("/", { replace: true });
        },
        page: requestParams.page,
        perPage: requestParams.perPage,
        q: searchQuery || undefined,
        statusFilter: status,
        setLoading,
        setApiError,

        setItems: (rows: IInquiryProps[]) =>
          applyServerResponse({ items: rows }),
        setTotal: (t: number) => applyServerResponse({ meta: { total: t } }),
        setPage: (p: number) => applyServerResponse({ meta: { page: p } }),
        setPerPage: (pp: number) =>
          applyServerResponse({ meta: { perPage: pp } }),
        setResolvedTotal: (n: number) =>
          applyServerResponse({ meta: { resolvedTotal: n } }),
        setUnresolvedTotal: (n: number) =>
          applyServerResponse({ meta: { unresolvedTotal: n } }),
      })
      .catch(() => { });
  }, [requestParams.page, requestParams.perPage, filter, searchQuery]);

  const listForRender = filteredInquiries;

  return (
    <main className={styles.page} data-testid="ci-page">
      <div
        className={styles.head}
        role="region"
        aria-label="User Contact Inquiries toolbar"
      >
        <h1 className={styles.title}>User Contact Inquiries</h1>

        <div className={styles.toolbar} data-testid="ci-toolbar">
          <div ref={filterRef} className={styles.filterWrap}>
            <button
              type="button"
              className={styles.filterBtn}
              onClick={() => setFilterOpen((o) => !o)}
              aria-haspopup="listbox"
              aria-expanded={filterOpen}
              data-testid="ci-filter-button"
            >
              <span>Status</span>
              <strong className={styles.filterCurrent}>
                {filter === "all"
                  ? "All"
                  : filter === "resolved"
                    ? "Resolved"
                    : "Unresolved"}
              </strong>
              <Chevron
                className={`${styles.chevIcon} ${filterOpen ? styles.chevOpen : ""
                  }`}
                aria-hidden="true"
              />
            </button>

            {filterOpen && (
              <ul
                role="listbox"
                tabIndex={-1}
                className={styles.filterMenu}
                data-testid="ci-filter-menu"
              >
                <li
                  role="option"
                  aria-selected={filter === "all"}
                  className={`${styles.filterItem} ${filter === "all" ? styles.filterActive : ""
                    }`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => chooseFilter("all")}
                  data-testid="ci-filter-all"
                >
                  <span>All</span>
                  <span className={styles.badge}>{allCount}</span>
                </li>
                <li
                  role="option"
                  aria-selected={filter === "resolved"}
                  className={`${styles.filterItem} ${filter === "resolved" ? styles.filterActive : ""
                    }`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => chooseFilter("resolved")}
                  data-testid="ci-filter-resolved"
                >
                  <span>Resolved</span>
                  <span className={`${styles.badge} ${styles.badgeOk}`}>
                    {resCount}
                  </span>
                </li>
                <li
                  role="option"
                  aria-selected={filter === "unresolved"}
                  className={`${styles.filterItem} ${filter === "unresolved" ? styles.filterActive : ""
                    }`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => chooseFilter("unresolved")}
                  data-testid="ci-filter-unresolved"
                >
                  <span>Unresolved</span>
                  <span className={`${styles.badge} ${styles.badgeWarn}`}>
                    {unresCount}
                  </span>
                </li>
              </ul>
            )}
          </div>

          <div className={styles.Cisearchbar}>
            <SearchBar
              value={searchQuery}
              onChange={(v) => {
                setSearchQuery(v);
                setPage(1);
              }}
              placeholder="Search inquiries…"
              data-testid="ci-searchbar"
            />
          </div>

          <div className={styles.pager} data-testid="ci-pager">
            <div
              ref={perPageDropdownWrapRef}
              className={styles.filterWrap}
              data-testid="ci-perpage-wrap"
            >
              <span className={styles.perPageLabel}>Per page </span>
              <button
                ref={perPageDropdownBtnRef}
                type="button"
                className={styles.filterBtn}
                aria-haspopup="listbox"
                aria-expanded={perPageDropdownOpen}
                aria-controls="ci-perpage-menu"
                onClick={togglePerPageDropdown}
                onKeyDown={onPerPageDropdownButtonKeyDown}
                data-testid="ci-perpage-button"
              >
                {perPage}
                <Chevron
                  className={`${styles.chevIcon} ${perPageDropdownOpen ? styles.chevOpen : ""
                    }`}
                  aria-hidden
                />
              </button>

              {perPageDropdownOpen && (
                <ul
                  id="ci-perpage-menu"
                  ref={perPageDropdownListRef}
                  role="listbox"
                  tabIndex={-1}
                  className={styles.filterMenu}
                  onKeyDown={onPerPageDropdownListKeyDown}
                  aria-activedescendant={`ci-ppd-opt-${perPageDropdownActiveIndex}`}
                  data-testid="ci-perpage-menu"
                >
                  {perPageOptions.map((option, index) => {
                    const isSelected = option === perPage;
                    const isActive = index === perPageDropdownActiveIndex;
                    return (
                      <li
                        id={`ci-ppd-opt-${index}`}
                        key={option}
                        role="option"
                        aria-selected={isSelected}
                        className={[
                          styles.filterItem,
                          isSelected ? styles.filterActive : "",
                          isActive ? "" : "",
                        ].join(" ")}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectPerPageAt(index)}
                        data-testid={`ci-perpage-option-${option}`}
                      >
                        {option}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {totalPages !== null && (
              <Fragment>
                <button
                  className={styles.navBtn}
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1 || loading}
                  data-testid="ci-prev"
                >
                  Prev
                </button>
                <span
                  className={styles.pageIndicator}
                  data-testid="ci-indicator"
                >
                  {page} / {totalPages}
                </span>
                <button
                  className={styles.navBtn}
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages || loading}
                  data-testid="ci-next"
                >
                  Next
                </button>
              </Fragment>
            )}
          </div>
        </div>
      </div>

      {loading && (
        <div className={styles.loading} data-testid="ci-loading">
          Loading inquiries…
        </div>
      )}
      {!loading && apiError && (
        <div className={styles.error} role="alert" data-testid="ci-error">
          {apiError}
        </div>
      )}

      {!loading && !apiError && listForRender.length === 0 && (
        <div className={styles.empty} data-testid="ci-empty">
          No inquiries found{searchQuery ? ` for “${searchQuery}”` : ""}.
        </div>
      )}

      {!loading && !apiError && listForRender.length > 0 && (
        <section className={styles.list} data-testid="ci-list">
          {listForRender.map((q) => (
            <ContactInquiryCard
              key={q.id}
              inquiry={q}
              isOpen={!!expanded[q.id]}
              onToggle={() => toggleExpand(q.id)}
              firstSentence={(txt) => {
                const t = (txt ?? "").trim();
                if (!t) return "";
                const m = t.match(/.*?[.!?](\s|$)/);
                const s = (m ? m[0] : t.split(/\n+/)[0]).trim();
                return s.length < t.length ? s + "…" : s;
              }}
              formatWhen={formatWhen}
              onOpenModal={() => handleOpenReply(q)}
              onResolve={() => handleResolve(q)}
            />
          ))}
        </section>
      )}

      {replyOpen && activeInquiry && (
        <ReplyToInquiryModal
          open={replyOpen}
          contactId={activeInquiry.id}
          title={activeInquiry.subject}
          originalMessage={activeInquiry.message}
          onClose={handleCloseReply}
          onReplySuccess={handleReplySuccess}
        />
      )}
    </main>
  );
};

export default ContactInquiries;
